import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";

const FeedbackRow = ({ queryId }) => {
  const { t } = useLanguage();
  const [vote, setVote] = useState(null);
  const [loadingVote, setLoadingVote] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!queryId) {
      setVote(null);
      return;
    }

    let cancelled = false;
    setLoadingVote(true);

    (async () => {
      try {
        const res = await axios.get(`${API}/council/query/${queryId}/feedback`);
        const helpful = res.data?.data?.helpful;
        if (!cancelled && typeof helpful === "boolean") {
          setVote(helpful);
        } else if (!cancelled) {
          setVote(null);
        }
      } catch {
        if (!cancelled) {
          setVote(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingVote(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [queryId]);

  const recordVote = async (helpful) => {
    if (!queryId || vote !== null || submitting || loadingVote) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/council/query/${queryId}/feedback`, { helpful });
      setVote(helpful);
      toast.success(t("query.feedbackThanks"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!queryId) return null;

  const disabled = vote !== null || submitting || loadingVote;

  return (
    <div
      className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-border"
      data-testid="feedback-row"
    >
      <span className="text-xs text-muted-foreground">{t("query.feedbackPrompt")}</span>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant={vote === true ? "default" : "outline"}
          size="sm"
          className="h-7 w-7 p-0"
          disabled={disabled}
          aria-label={t("query.feedbackHelpful")}
          aria-pressed={vote === true}
          onClick={() => recordVote(true)}
          data-testid="feedback-helpful-btn"
        >
          <ThumbsUp className="w-3.5 h-3.5" />
        </Button>
        <Button
          type="button"
          variant={vote === false ? "default" : "outline"}
          size="sm"
          className="h-7 w-7 p-0"
          disabled={disabled}
          aria-label={t("query.feedbackNotHelpful")}
          aria-pressed={vote === false}
          onClick={() => recordVote(false)}
          data-testid="feedback-not-helpful-btn"
        >
          <ThumbsDown className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default FeedbackRow;
